<template>
    <AssetSignatureDocumentsTable>
        <template #actions>
            <DeleteBulkButton
                v-if="selected.length > 0"
                :selected="selected"
                @click.stop="bulkDeleteModalOpen = true"
            />
            <CreateButton
                title="create"
                @click="createSignatureDocumentModalOpen = true"
            />
        </template>
    </AssetSignatureDocumentsTable>
    <DeleteItemsModal
        v-if="bulkDeleteModalOpen"
        id="delete-assets-modal"
        ref="deleteAssetsModal"
        v-model="signatureDocumentsToDelete"
        v-model:open="bulkDeleteModalOpen"
        title="modal_title_delete_signature_documents"
        id-key="uuid"
        name-key="name"
        @submit="bulkDeleteSignatureDocuments"
        @cancel="bulkDeleteModalOpen = false"
    />
    <CreateSignatureDocumentModal
        v-if="createSignatureDocumentModalOpen"
        v-model:open="createSignatureDocumentModalOpen"
        @submit="uploadSignatureDocument"
    />
    <UpdateSignatureDocumentModal
        v-if="updateSignatureDocumentModalOpen"
        v-model:open="updateSignatureDocumentModalOpen"
        :model-value="signatureDocumentToUpdate"
        @submit="
            (signatureDocument) => updateSignatureDocument(signatureDocument)
        "
    />
</template>

<script setup lang="ts">
import DeleteItemsModal from '@src/components/DeleteItemsModal.vue'
import { useTable } from '@src/composables/common'
import { Asset, EmissionSignatureDocument } from '@src/types'
import {
    bulkDeleteAssetSignatureDocuments,
    getAssetSignatureDocuments,
    updateAssetSignatureDocument,
    uploadAssetSignatureDocument,
} from '@src/utils/api/assets'
import { useLanguageStore } from '@src/utils/stores/languages'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import CreateButton from '../actions/CreateButton.vue'
import DeleteBulkButton from '../actions/DeleteBulkButton.vue'
import CreateSignatureDocumentModal from './CreateSignatureDocumentModal.vue'
import UpdateSignatureDocumentModal from './UpdateSignatureDocumentModal.vue'

const props = defineProps<{
    asset: Asset
}>()

const route = useRoute()
const router = useRouter()
const toast = useToastStore()
const i18n = useI18n()
const userStore = useUserStore()
const languageStore = useLanguageStore()

const signatureDocuments = ref<EmissionSignatureDocument[]>([])
const createSignatureDocumentModalOpen = ref(false)
const bulkDeleteModalOpen = ref(false)
const signatureDocumentsToDelete = ref<EmissionSignatureDocument | null>(null)
const updateSignatureDocumentModalOpen = ref(false)
const signatureDocumentToUpdate = ref<EmissionSignatureDocument | null>(null)

const loadSignatureDocuments = async () => {
    if (props.asset.asset_uid) {
        const response = await getAssetSignatureDocuments(props.asset.asset_uid)
        signatureDocuments.value = response.data.documents
    }
}

const { TableWrapper: AssetSignatureDocumentsTable, selected } = useTable({
    loadData: loadSignatureDocuments,
    filters: {},
    tableProps: {
        id: 'signature-documents-table',
        label: 'signature_documents',
        help: 'help_signature_documents_table',
        pagination: true,
        data: signatureDocuments,
        total: signatureDocuments.value.length,
        columns: [
            {
                label: 'ID',
                key: 'uuid',
                select: true,
            },
            {
                label: 'ID',
                key: 'uuid',
            },
            {
                label: 'Name',
                key: 'name',
            },
            {
                label: 'language',
                key: 'language',
                cellRenderer: (props: {
                    rowData: EmissionSignatureDocument
                }) => {
                    return (
                        languageStore.languages.find(
                            (language) => language.value === props.rowData.lang
                        )?.label || props.rowData.lang
                    )
                },
            },
            {
                label: 'created_at',
                key: 'created_at',
                cellRenderer: (props: {
                    rowData: EmissionSignatureDocument
                }) => {
                    return moment(props.rowData.created_on).format('DD.MM.yyyy')
                },
            },
        ],
        actions: [
            {
                action: (rowData: EmissionSignatureDocument) => {
                    const url = rowData.url as string
                    if (url) {
                        window.open(url)
                    }
                },
                icon: 'fa-eye',
                title: 'view',
            },
            {
                action: (rowData: EmissionSignatureDocument) => {
                    updateSignatureDocumentModalOpen.value = true
                    signatureDocumentToUpdate.value = {
                        ...rowData,
                    } as EmissionSignatureDocument
                },
                icon: 'fa-solid fa-pen',
                title: 'edit',
                condition: userStore.can('update_asset'),
            },
            {
                action: async (rowData: EmissionSignatureDocument) => {
                    bulkDeleteModalOpen.value = true
                    signatureDocumentsToDelete.value = [{ uuid: rowData.uuid }]
                    console.log(signatureDocumentsToDelete.value)
                },
                icon: 'fas fa-trash-alt',
                title: 'delete',
                color: 'text-danger-500',
                condition: userStore.can('update_asset'),
            },
        ],
    },
})

onMounted(() => {
    loadSignatureDocuments()
})

const bulkDeleteSignatureDocuments = async () => {
    if (signatureDocumentsToDelete.value.length > 0) {
        const response = await bulkDeleteAssetSignatureDocuments(
            props.asset,
            signatureDocumentsToDelete.value.map((signatureDocument) => {
                return signatureDocument?.uuid ?? ''
            })
        )
        if (response.data.success) {
            bulkDeleteModalOpen.value = false
            loadSignatureDocuments()
            selected.value = []
            const newQuery = { ...route.query }
            delete newQuery['signature-documents-table_selected']
            router.replace({ query: newQuery })
            toast.success(i18n.t('signature_document_deleted'))
        }
    }
}

const uploadSignatureDocument = async (
    signatureDocument: EmissionSignatureDocument
) => {
    const response = await uploadAssetSignatureDocument(
        props.asset,
        signatureDocument
    )
    if (response.data.success) {
        createSignatureDocumentModalOpen.value = false
        signatureDocuments.value = response.data.documents
        selected.value = []
        const newQuery = { ...route.query }
        delete newQuery['signature-documents-table_selected']
        router.replace({ query: newQuery })
        toast.success(i18n.t('signature_document_uploaded'))
    }
}

const updateSignatureDocument = async (
    signatureDocument: EmissionSignatureDocument
) => {
    const response = await updateAssetSignatureDocument(
        props.asset,
        signatureDocument
    )
    if (response.data.success) {
        updateSignatureDocumentModalOpen.value = false
        loadSignatureDocuments()
        toast.success(i18n.t('signature_document_updated'))
    }
}

watch(
    () => selected.value,
    (newSelected) => {
        signatureDocumentsToDelete.value = newSelected.map((id) =>
            signatureDocuments.value.find(
                (signatureDocument: EmissionSignatureDocument) =>
                    signatureDocument.uuid === id
            )
        )
    }
)
</script>
